import * as React from 'react';
import Box from '@mui/material/Box';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import MuiSelect, { SelectChangeEvent } from '@mui/material/Select';
import IconButton from '@mui/material/IconButton';
import ClearIcon from '@mui/icons-material/Clear';

interface Option {
	id: string;
	display: string;
}

interface SelectProps {
	label: string;
	value: string | string[];
	onChange: (event: SelectChangeEvent) => void;
	options: Option[];
	fullWidth?: boolean;
	minWidth?: number;
	disabled?: boolean;
	className?: string;
	required?: boolean;
	multiple?: boolean;
	clearable?: boolean;
	onClear?: () => void;
	placeholder?: string;
	error?: boolean;
	helperText?: string;
}

const Select: React.FC<SelectProps> = ({
	label,
	value,
	onChange,
	options,
	fullWidth = true,
	minWidth = 120,
	disabled = false,
	className,
	required = false,
	multiple = false,
	clearable = false,
	onClear,
	placeholder = '',
	error = false,
	helperText,
	...rest
}) => {
	const hasValue = multiple
		? Array.isArray(value) && value.length > 0
		: value !== '' && value !== null && value !== undefined;

	return (
		<Box sx={{ minWidth, position: 'relative' }} className={className}>
			<FormControl
				fullWidth={fullWidth}
				disabled={disabled}
				error={error}
			>
				<InputLabel id={`${label}-label`} shrink required={required}>
					{label}
				</InputLabel>
				<MuiSelect
					labelId={`${label}-label`}
					id={`${label}-select`}
					value={value as any}
					label={label}
					onChange={onChange}
					size="small"
					variant="outlined"
					required={required}
					notched
					multiple={multiple}
					displayEmpty
					renderValue={(selected) => {
						if (!hasValue && placeholder) {
							return (
								<span style={{ color: '#999' }}>
									{placeholder}
								</span>
							);
						}
						if (multiple && Array.isArray(selected)) {
							return selected
								.map(
									(val) =>
										options.find((opt) => opt.id === val)
											?.display
								)
								.filter(Boolean)
								.join(', ');
						}
						return (
							options.find((opt) => opt.id === selected)
								?.display || ''
						);
					}}
					{...rest}
				>
					{options.map((option) => (
						<MenuItem key={option.id} value={option.id}>
							{option.display}
						</MenuItem>
					))}
				</MuiSelect>
				{helperText && <FormHelperText>{helperText}</FormHelperText>}
			</FormControl>
			{clearable && hasValue && onClear && (
				<IconButton
					onClick={onClear}
					size="small"
					sx={{
						position: 'absolute',
						right: 28,
						top: '50%',
						transform: 'translateY(-50%)',
						padding: '4px',
						color: (theme) => theme.palette.text.secondary,
						'&:hover': {
							color: (theme) => theme.palette.error.main,
						},
						zIndex: 1,
					}}
					title="Clear selection"
				>
					<ClearIcon fontSize="small" />
				</IconButton>
			)}
		</Box>
	);
};

export default Select;
